// Спільне опитування SCADA-ендпоінтів і оцінка свіжості телеметрії.
//
// Статус-смуга станції, вкладки «Інвертори» та «УЗЕ» читають ті самі
// /scada/*/latest. Власний setInterval у кожного компонента давав би кілька
// однакових запитів на кожен тік. Тут один запит на URL, скільки б не було
// підписників; останній відписаний зупиняє таймер. Останній знімок лишається
// в кеші, тож при перемиканні вкладок дані з'являються одразу — а їхній вік
// чесно показує, наскільки вони свіжі.
import i18n from '@/i18n';
import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { scada } from '@/scada/tokens';

interface Entry {
  data: unknown;
  err: boolean;
  at: number;
  ms: number;
  subs: Set<() => void>;
  timer?: ReturnType<typeof setInterval>;
}

const cache = new Map<string, Entry>();

async function load(url: string) {
  const e = cache.get(url);
  if (!e) return;
  try {
    const r = await authFetch(`${API_BASE_URL}${url}`);
    if (!r.ok) throw new Error(String(r.status));
    e.data = await r.json();
    e.err = false;
  } catch {
    // Попередній знімок НЕ стираємо: одинична невдала спроба не робить
    // ще чинні дані невідомими (ТЗ EMS box, § 5). Їхній вік покаже застарівання.
    e.err = true;
  }
  e.at = Date.now();
  e.subs.forEach((f) => f());
}

export function useSharedPoll<T>(url: string | null, ms = 30000): { data: T | null; err: boolean; fetchedAt: number } {
  const [, force] = useState(0);
  useEffect(() => {
    if (!url) return;
    let e = cache.get(url);
    if (!e) {
      e = { data: null, err: false, at: 0, ms, subs: new Set() };
      cache.set(url, e);
    }
    const entry = e;
    const tick = () => force((n) => n + 1);
    entry.subs.add(tick);
    if (!entry.timer) {
      load(url);
      entry.timer = setInterval(() => load(url), entry.ms);
    } else if (entry.at) {
      tick();
    }
    return () => {
      entry.subs.delete(tick);
      if (entry.subs.size === 0 && entry.timer) {
        clearInterval(entry.timer);
        entry.timer = undefined;
      }
    };
  }, [url, ms]);
  const e = url ? cache.get(url) : undefined;
  return { data: (e?.data as T | undefined) ?? null, err: e?.err ?? false, fetchedAt: e?.at ?? 0 };
}

// ─── Свіжість ────────────────────────────────────────────────────────────────

/** Стан звʼязку за віком останнього знімка. «unknown» — знімка немає взагалі. */
export type Link = 'online' | 'stale' | 'lost' | 'unknown';

export interface Cadence {
  /** До цього віку знімок вважається свіжим. */
  onlineSec: number;
  /** Після цього віку — звʼязку немає; між порогами дані застарівають. */
  staleSec: number;
  /** Цикл колектора — для підпису на екрані. */
  label: string;
}

// Колектори мають різну каденцію: Лучки — GoodWe ~61 с і УЗЕ ~60 с, решта СЕС —
// Modbus-колектор раз на ~5 хв. Поріг «на звʼязку» = три пропущені цикли,
// «немає звʼязку» для решти — ті самі 40 хв, що вже діють в адаптері SSE.
const CADENCE: Record<string, Cadence> = {
  luchky: { onlineSec: 180, staleSec: 900, get label() { return i18n.t('ak_scada_collector_cycle_1_min'); } },
};
const DEFAULT_CADENCE: Cadence = { onlineSec: 900, staleSec: 2400, get label() { return i18n.t('ak_scada_collector_cycle_5_min'); } };

export const cadenceOf = (stationId?: string | null): Cadence =>
  (stationId && CADENCE[stationId]) || DEFAULT_CADENCE;

export function linkOf(ageSec: number | null | undefined, c: Cadence): Link {
  if (ageSec == null || !Number.isFinite(ageSec)) return 'unknown';
  if (ageSec <= c.onlineSec) return 'online';
  if (ageSec <= c.staleSec) return 'stale';
  return 'lost';
}

/**
 * Вік знімка в секундах. `readAtLocal` з API — київський настінний час із
 * суфіксом «Z» (не UTC!), тож порівнюємо з поточним київським часом,
 * записаним так само. Прямий Date.parse дав би вік на 2–3 год у мінус, і
 * будь-який застарілий знімок виглядав би свіжим.
 */
export function ageSecOfLocal(readAtLocal?: string | null): number | null {
  if (!readAtLocal) return null;
  const nowKyiv = Date.parse(new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Kyiv' }).replace(' ', 'T') + 'Z');
  const t = Date.parse(readAtLocal);
  if (!Number.isFinite(t) || !Number.isFinite(nowKyiv)) return null;
  return Math.max(0, (nowKyiv - t) / 1000);
}

/** Годинник знімка «15:19» з київського настінного часу. */
export const clockOfLocal = (readAtLocal?: string | null) => (readAtLocal ? readAtLocal.slice(11, 16) : '—');

export function fmtAge(sec: number | null | undefined): string {
  if (sec == null) return i18n.t('ak_scada_no_data');
  if (sec < 60) return i18n.t('ak_scada_s_ago', { n: Math.round(sec) });
  if (sec < 3600) return i18n.t('ak_scada_min_ago', { n: Math.round(sec / 60) });
  if (sec < 86400) return i18n.t('ak_scada_h_min_ago', { h: Math.floor(sec / 3600), m: Math.round((sec % 3600) / 60) });
  return i18n.t('ak_scada_d_ago', { n: Math.floor(sec / 86400) });
}

export const LINK_STYLE: Record<Link, { c: string; label: string }> = {
  online: { c: scada.state.ok, get label() { return i18n.t('ak_scada_online'); } },
  stale: { c: scada.state.warning, get label() { return i18n.t('ak_scada_data_is_getting_stale'); } },
  lost: { c: scada.state.offline, get label() { return i18n.t('ak_scada_no_link_2'); } },
  unknown: { c: scada.state.offline, get label() { return i18n.t('ak_scada_no_data'); } },
};

// ─── Форматування ────────────────────────────────────────────────────────────

export const nfMk = (o: Intl.NumberFormatOptions) => {
  const cache: Record<string, Intl.NumberFormat> = {};
  return { format: (v: number) => { const l = i18n.language === 'en' ? 'en-GB' : 'uk-UA'; return (cache[l] || (cache[l] = new Intl.NumberFormat(l, o))).format(v); } };
};
const nf0 = nfMk({ maximumFractionDigits: 0 });
const nf1 = nfMk({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = nfMk({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Невідоме — «—», а не нуль. */
export const kw0 = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—' : nf0.format(v));
export const n1 = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—' : nf1.format(v));
export const mw2 = (kw: number | null | undefined) => (kw == null || !Number.isFinite(kw) ? '—' : nf2.format(kw / 1000));
/** Знакове значення: «+1 220» / «−640» (типографський мінус). */
export const signed0 = (v: number | null | undefined) =>
  v == null || !Number.isFinite(v) ? '—' : `${v > 0.5 ? '+' : v < -0.5 ? '−' : ''}${nf0.format(Math.abs(v))}`;

/** Стан «N з M»: усі — ONLINE, частина — PARTIAL, жодного — OFFLINE. */
export type Fill = 'online' | 'partial' | 'offline' | 'unknown';
export function fillOf(ok: number | null | undefined, total: number | null | undefined): Fill {
  if (ok == null || !total) return 'unknown';
  if (ok >= total) return 'online';
  if (ok <= 0) return 'offline';
  return 'partial';
}
export const FILL_STYLE: Record<Fill, { c: string; label: string }> = {
  online: { c: scada.state.ok, label: 'ONLINE' },
  partial: { c: scada.state.warning, label: 'PARTIAL' },
  offline: { c: scada.state.offline, label: 'OFFLINE' },
  unknown: { c: scada.state.offline, label: '—' },
};
